import type { AxiosInstance } from 'axios';
import type { QueryClient } from '@tanstack/react-query';
import type { ApiCatalogFacets, ApiCatalogFacetMake } from 'src/types/common';
import type { CatalogVertical, CatalogFacetsParams } from 'src/utils/catalogUrl';

import { useQuery, queryOptions, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { READ_TIMEOUT_MS } from 'src/utils/requestError';

import { QueryKeys } from './QueryKeys';
import { LIVE_STALE_TIME } from './staleness';
import { seedCatalogEntries } from './useCatalogEntry';
import { askUnlessServerAnswered } from './serverRendered';

const FACETS_PATH: Record<CatalogVertical, string> = {
  buy: '/buyer/listings/facets',
  rent: '/buyer/rentals/facets',
};

/** How soon lists the api refused are asked for again, on their own. */
export const REFUSED_LISTS_RETRY_MS = 10_000;

/** The two 4xx that say nothing of the question: the api ran out of time (408), or is asked too often (429). */
const NOT_NOW = [408, 429];

/**
 * A 4xx is the api's verdict on the question itself: asking the same thing again changes nothing.
 * A 408 and a 429 are its word on the moment instead — "try again shortly" is what the rate limiter
 * says in so many words — and those are refusals like any other.
 */
const isVerdict = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null)?.status;

  return typeof status === 'number' && status >= 400 && status < 500 && !NOT_NOW.includes(status);
};

/**
 * The lists of a catalog's filter as a query. Every make and model of a good answer is handed to
 * the address lookups of `client` (`seedCatalogEntries`). Asked by the hook below and by the
 * server's render of a page that shows the filter column (`src/server/catalog.server.ts`).
 */
export const catalogFacetsQuery = (
  vertical: CatalogVertical,
  params: CatalogFacetsParams,
  client: QueryClient,
  http: Pick<AxiosInstance, 'get'> = axios
) =>
  queryOptions({
    queryKey: QueryKeys.catalogFacets(vertical, params),
    queryFn: async (): Promise<ApiCatalogFacets> => {
      const response = await http.get<ApiCatalogFacets>(FACETS_PATH[vertical], {
        params,
        timeout: READ_TIMEOUT_MS,
      });
      const { total, makes } = response.data ?? {};

      if (!Array.isArray(makes)) {
        throw new Error(`${FACETS_PATH[vertical]} answered without its makes`);
      }

      // A make that came without its models has none to offer; nothing downstream has to ask twice.
      const listed: ApiCatalogFacetMake[] = makes.map((make) => ({
        ...make,
        models: Array.isArray(make.models) ? make.models : [],
      }));

      seedCatalogEntries(client, listed);

      return { total: typeof total === 'number' ? total : 0, makes: listed };
    },
    staleTime: LIVE_STALE_TIME,
  });

/**
 * The makes and models a catalog's filter offers (storefront-catalog spec §4.4): only those that
 * have a car in the feed under the same filters, each with its count. `params` are the feed's own
 * filters less the make, the model and the page (`toSaleFacetsParams` / `toRentalFacetsParams`),
 * so a count is what the feed would list once that make is chosen.
 *
 * Asked again whenever those filters change, whenever a panel mounts (a visit back to the catalog)
 * and when the person refreshes the list — not when a make, a model or a page is picked: none of
 * the three changes what is counted. The previous answer stays in the lists while the next one is
 * on its way. The one mount that does not ask is the first after the server rendered the page with
 * these very lists.
 *
 * An answer that is not the lists — a body of another shape behind a 200 — is a refusal, not an
 * empty catalog: read as "no make has a car", it would put "(0)" beside the very make whose cars
 * the grid is showing. Every make and model of a good answer is handed to the address lookups
 * (`seedCatalogEntries`).
 *
 * Refused lists are asked for again on their own, every ten seconds, until the api gives them: no
 * screen has a button for the lists alone (the rent catalog has no Refresh at all), and a restart
 * of the api in the middle of a visit must not leave a filter with nothing to choose from until a
 * reload. A 4xx that judges the question is not asked again.
 */
export function useCatalogFacets(vertical: CatalogVertical, params: CatalogFacetsParams) {
  const client = useQueryClient();

  return useQuery({
    ...catalogFacetsQuery(vertical, params, client),
    refetchOnMount: askUnlessServerAnswered,
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      query.state.status === 'error' && !isVerdict(query.state.error) ? REFUSED_LISTS_RETRY_MS : false,
  });
}
